/* eslint-disable import/no-cycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers.
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
/* eslint-disable no-param-reassign */

import {
  Component, Inject, Input, OnInit,
  QueryList, Renderer2,
  ViewChild, ViewChildren, OnChanges,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import moment from 'moment';
import { cloneDeep } from 'lodash-es';

import { ButtonSize, ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { AeroCalendarPicker } from '../../aero/aero-calendar-picker/aero-calendar-picker.model';
import { AeroFormComponent } from '../../aero/aero-form/aero-form.component';
import { ResumeComponent } from '../resume.component';
import { ResumeExperienceSubSection } from './experience-sub-section.model';
import { ResumeSection } from '../section/section.model';
import { ResumeSectionType } from '../section/resume-section-type.enum';
import { ResumeSubSectionComponent } from '../sub-section/sub-section.component';
import { SectionDataAlertService } from '../section/section-data-alert.service';
import { TranslationService } from '../../common/translation.service';
import { UnitSimpleDTO } from '../content-selection-modal/unit-simple-dto.model';
import { BULB_BUSY_COVER_SERVICE } from '../../app/angularjs.providers';

/**
 * Angular component that will house the 'Experience' resume section.
 */
@Component({
  selector: 'bulb-resume-experience',
  templateUrl: './experience.component.html',
  standalone: false,
})
export class ResumeExperienceComponent implements OnInit, OnChanges {
  /**
   * Whether the current user can edit the section.
   */
  @Input() canEdit: boolean;

  /**
   * Work experience section model.
   */
  @Input() public experienceSection: ResumeSection;

  /**
   * Reference to the parent resume component.
   */
  @Input() public resumeComponent: ResumeComponent;

  /**
   * Date picker configuration for the work experience.
   */
  public datepickerConfig : AeroCalendarPicker = new AeroCalendarPicker();

  /**
   * Clone of the work experience sub-section data to use during edit, so any changes can be cancelled
   * or saved as needed.
   */
  public experienceSubSectionClone : Array<ResumeExperienceSubSection> = new Array<ResumeExperienceSubSection>();

  /**
   * If Accordion tab open set the value as true else false.
   */
  public isTabOpen: boolean = false;

  /**
   * Whether the section is showing the edit form or not.
   */
  public editMode: boolean = false;

  /**
   * Tracks for the tab changes were made in session.
   */
  public tabChange: boolean;

  /**
   * Whether the 'Add Entry' button is displayed in the view mode.
   */
  public showAddEntryButton: boolean = true;

  /**
   * Array of portfolio content that the user has removed.
   */
  public removedPortfolioContent : Array< UnitSimpleDTO > = new Array<UnitSimpleDTO>();

  /**
   * Reference to list of forms element in the DOM.
   */
  @ViewChildren(AeroFormComponent)
  public aeroFormComponentList: QueryList<AeroFormComponent> = new QueryList<AeroFormComponent>();

  /**
   * Aero form component reference to the currently selected entry in the resume experience subsection.
   */
  public aeroFormComponent: AeroFormComponent;

  /**
   * Boolean indicating if the user has saved the data in this section.
   */
  private hasSaved : boolean = false;

  /**
   * Reference to the resume experience sub-section
   */
  @ViewChild(ResumeSubSectionComponent) subSectionComponent: ResumeSubSectionComponent;

  /**
   * Index of subsection for editing the experience.
   */
  private isNewEntry: boolean;

  /**
   * Number indicating how many units a user has selected to display in their portfolio content.
   */
  private numberOfSelectedContent : number = 0;

  /**
   * Number indicating how many units a user has removed from their portfolio content.
   */
  public numberOfRemovedContent : number = 0;

  /*
   * Translated text for the end date placeholder.
   */
  public endDatePlaceholder: string = '';

  /**
   * Translated text for the checkbox label.
   */
  private checkboxLabel: string;

  /**
   * Button style for aero button
   */
  public buttonStyle: ButtonStyle = ButtonStyle.TEXT_WITH_ICON;

  /**
   * Button size for aero button
   */
  public buttonSize: ButtonSize = ButtonSize.LARGER;

  /**
   * Button style for add portfolio content button
   */
  public addPortfolioContentButtonStyle: ButtonStyle = ButtonStyle.PRIMARY_2;

  /**
   * Max length integer for text areas
   */
  public textAreaMaxLength : number = 160;

  /**
   * Max length of summary field
   */
  public maxLength: number = 1000;

  /**
   * flag for character limit reached.
   */
  public charLimitReached: boolean = false;

  /**
   * boolean indicating whether any subsection has logo.
   */
  public isLogoUploaded: boolean = false;

  /**
   * Index of currently editing subsection
   * @private
   */
  private currentIndex: number = -1;

  /**
   * Default Constructor.
   *
   * @param bulbModalService
   * @param BulbBusyCoverService
   * @param renderer
   * @param sectionDataAlertService
   * @param $translate
   */
  constructor(
    @Inject('BulbModalService') private bulbModalService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    private renderer: Renderer2,
    private sectionDataAlertService : SectionDataAlertService,
    private $translate: TranslationService,
  ) {
  }

  /**
   * Callback method called when image upload complete.
   * @param $event
   */
  uploadComplete($event) {
    this.experienceSubSectionClone[this.currentIndex].logoUrl = $event;
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[this.currentIndex];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent);
  }

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's
   * data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  public ngOnInit(): void {
    if (this.tabChange) {
      this.isTabOpen = JSON.parse(sessionStorage.getItem('experienceTabOpen'));
    } else if (!this.experienceSection.empty && !this.tabChange) {
      this.isTabOpen = true;
    } else {
      sessionStorage.experienceTabOpen = false;
    }
    this.cloneSubSection();
    ResumeExperienceComponent.configureCalender(this.datepickerConfig);
    this.translateKeywords();
    this.showAddEntryButton = this.canEdit;
  }

  /**
   * Called when @Input parameter for this component is updated.
   */
  public ngOnChanges(): void {
    // If the education section has been populated, we default the accordion tab to be open.
    if (!this.experienceSection.empty) {
      this.isTabOpen = true;
      sessionStorage.experienceTabOpen = true;
    }
    this.cloneSubSection();
  }

  /**
   * Clone the resume work experience section.
   */
  private cloneSubSection() {
    if (this.experienceSection.subSections.length > 0) {
      this.experienceSubSectionClone = <ResumeExperienceSubSection[]>cloneDeep(this.experienceSection.subSections);
      this.convertDateType();
      this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.experienceSubSectionClone);
    }
  }

  /**
   * Convert the date type for start date and end date for the experience.
   */
  convertDateType() {
    this.experienceSubSectionClone.forEach((section) => {
      section.startDate = (section.startDate) ? moment(section.startDate).toDate() : null;
      section.endDate = (section.endDate) ? moment(section.endDate).toDate() : null;
    });
  }

  /**
   * Set the aero-calender config picker.
   *
   * @param datepickerConfig configuration.
   */
  private static configureCalender(datepickerConfig: AeroCalendarPicker) {
    datepickerConfig.maxDate = new Date();
    datepickerConfig.minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
    datepickerConfig.range = `${datepickerConfig.minDate.getFullYear()}:${datepickerConfig.maxDate.getFullYear()}`;
  }

  /**
   * Cancel button handler.
   *
   * @param index of subsection for editing the experience.
   */
  public cancelEdit(index: number) {
    this.showAddEntryButton = true;
    if (this.experienceSection.empty) {
      this.isTabOpen = false;
      sessionStorage.experienceTabOpen = false;
    } else {
      this.editMode = false;
    }
    this.handleUnsavedPortfolioContent(index);
    if (this.aeroFormComponent) {
      this.aeroFormComponent.markAeroFormAsPristine();
      this.aeroFormComponent.updateSaveButtonState();
    }
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.disableAlert();
    if (this.isNewEntry) {
      this.experienceSubSectionClone.pop();
      this.isNewEntry = false;
    } else {
      this.cloneSubSection();
    }
  }

  /**
   * Response handler for edit event from section component which moves us into 'edit' mode.
   *
   * @param subSectionIndex Index of the current sub-section in the experience section.
   */
  public beginEdit(subSectionIndex: number = -1) {
    this.showAddEntryButton = false;
    this.editMode = true;
    this.isTabOpen = true;
    sessionStorage.experienceTabOpen = true;
    if (subSectionIndex < 0) {
      this.addEntry();
    } else {
      this.currentIndex = subSectionIndex;
      this.editEntryMode(subSectionIndex);
      setTimeout(() => {
        const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[subSectionIndex];
        this.sectionDataAlertService.enableAlert(aeroFormComponent, () => {
          this.cancelEdit(subSectionIndex);
        });
      });
    }
  }

  /**
   * Handle closing of the main accordion section for the resume experience header tab click.
   */
  public handleTabClose() : void {
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.isTabOpen = false;
    sessionStorage.experienceTabOpen = false;
    this.editMode = false;
  }

  /**
   * Handles open of tab
   */
  handleTabOpen() {
    this.isTabOpen = true;
    sessionStorage.experienceTabOpen = true;
  }

  /**
   * Save the work experience details.
   *
   * @param experienceForm
   * @param index of subsection for editing the experience.
   */
  public async save(experienceForm: NgForm, index: number) {
    const selectedSubsection: any = <ResumeExperienceSubSection>cloneDeep(this.experienceSubSectionClone[index]);
    if (selectedSubsection && selectedSubsection.workHighlights) {
      selectedSubsection.workHighlights = selectedSubsection.workHighlights.filter((highlight) => highlight !== null && highlight !== undefined && highlight !== ''
          && !(typeof highlight === 'object' && Object.keys(highlight).length === 0));
    }
    // Convert the start date in YYYY-MM-DD format.
    selectedSubsection.startDate = experienceForm.value.startDate
      ? moment(experienceForm.value.startDate).format('YYYY-MM-DD') : null;
    // Convert the end date in YYYY-MM-DD format.
    selectedSubsection.endDate = experienceForm.value.endDate
      ? moment(experienceForm.value.endDate).format('YYYY-MM-DD') : null;

    if (selectedSubsection.portfolioContent && selectedSubsection.portfolioContent.length > 0) {
      // Update the clone with the attached portfolio content
      const portfolioContentDTO: Array<any> = new Array<any>();
      selectedSubsection.portfolioContent.forEach((unit) => {
        const unitDTO: any = {
          id: unit.id,
        };
        portfolioContentDTO.push(unitDTO);
      });
      selectedSubsection.portfolioContent = portfolioContentDTO;
    }

    if (index >= 0) {
      this.experienceSection.subSections[index] = selectedSubsection;
    } else {
      this.experienceSection.subSections.push(selectedSubsection);
    }

    this.sortEntriesByDate();
    delete selectedSubsection.editMode;
    delete selectedSubsection.endDatePlaceholder;

    this.experienceSection.empty = false;
    const savePromise = this.resumeComponent.save().then((response) => {
      this.hasSaved = true;
      this.isNewEntry = false;
      this.editMode = false;
      this.experienceSection.subSections = response.sections
        .find((section) => (section.type === ResumeSectionType.Experience)).subSections;
      experienceForm.resetForm(experienceForm.value);
      this.showAddEntryButton = true;
      this.cloneSubSection();
    });
    this.BulbBusyCoverService.showBusyDuring(savePromise);
    this.sectionDataAlertService.resetPortfolioContentModifiedMap();
    this.sectionDataAlertService.disableAlert();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.experienceSubSectionClone);
  }

  /**
   * Reset the end date and set the placeholder.
   *
   * @param isChecked true/false.
   * @param index of subsection for editing the experience.
   */
  public resetEndDate(isChecked: boolean, index: number) {
    this.handleInputModelChanges(undefined, index);
    if (isChecked) {
      this.experienceSubSectionClone[index].endDatePlaceholder = this.endDatePlaceholder;
    } else {
      this.experienceSubSectionClone[index].endDatePlaceholder = '';
    }
  }

  /**
   * Handle adding the user's potential portfolio content to experience section.
   *
   * @param index for the edited experience section.
   */
  public addPortfolioContent(index : number) : void {
    this.subSectionComponent.addPortfolioContent(this, index);
  }

  /**
   * Helper method to update the portoFolioContent object identity so that the Angular detection cycle could detect
   * the object change after the removal of any portfolio content.
   *
   * @param event
   * @param index index for the edited experience section.
   */
  public onPortfolioContentRemoved(event : any, index : number) : void {
    this.subSectionComponent.removePortfolioContent(this, index);
  }

  /**
   * Method that handles removing any unsaved portfolio content from the view.
   *
   * @param index for the edited experience section.
   */
  private handleUnsavedPortfolioContent(index : number) : void {
    this.subSectionComponent.handleUnsavedPortfolioContent(this, index);
  }

  /**
   * Method to add new entry in the section.
   */
  public addEntry() {
    this.showAddEntryButton = false;
    this.isNewEntry = true;
    this.editMode = true;

    if (this.experienceSection.subSections.length === 0) {
      this.experienceSubSectionClone = new Array<ResumeExperienceSubSection>();
    }

    if (this.aeroFormComponent) {
      this.aeroFormComponent.resetFormFields();
      this.aeroFormComponent.form.resetForm(this.aeroFormComponent.form.value);
    }

    const selectedSubsection = new ResumeExperienceSubSection();
    selectedSubsection.editMode = true;
    this.experienceSubSectionClone.push(selectedSubsection);
    this.setFocusAndAttribute(this.experienceSubSectionClone.length - 1);
    setTimeout(() => {
      const sectionIndex = this.experienceSubSectionClone.length - 1;
      const aeroFormComponent : AeroFormComponent = this.aeroFormComponentList.toArray()[sectionIndex];
      this.sectionDataAlertService.enableAlert(aeroFormComponent, () => { this.cancelEdit(sectionIndex); });
      this.currentIndex = sectionIndex;
    });
  }

  /**
   * Populate selected entry in the form for editing.
   * @param index - selected entry index.
   */
  private editEntryMode(index: number) {
    if (this.isNewEntry) {
      this.experienceSubSectionClone.pop();
      this.isNewEntry = false;
    }

    this.resetEditMode();
    this.experienceSubSectionClone[index].editMode = true;
    if (this.experienceSubSectionClone[index].current) {
      this.translateKeywords();
      this.experienceSubSectionClone[index].endDatePlaceholder = this.endDatePlaceholder;
    }
    this.setFocusAndAttribute(index);
    this.hasSaved = false;
  }

  /**
   * Reset the edit mode for the experience.
   */
  private resetEditMode() {
    this.experienceSubSectionClone.forEach((section) => {
      section.editMode = false;
    });
  }

  /**
   * Sort the entries based on dates.
   */
  private sortEntriesByDate() {
    this.experienceSection.subSections.sort(
      (subsection1: ResumeExperienceSubSection, subsection2: ResumeExperienceSubSection) => {
        const firstObjectStartDate = (subsection1.startDate) ? moment(subsection1.startDate).valueOf() : 0;
        const firstObjectEndDate = (subsection1.endDate) ? moment(subsection1.endDate).valueOf() : 0;
        const secondObjectStartDate = (subsection2.startDate) ? moment(subsection2.startDate).valueOf() : 0;
        const secondObjectEndDate = (subsection2.endDate) ? moment(subsection2.endDate).valueOf() : 0;

        if (subsection1.current) {
          if (subsection2.current) {
            return (firstObjectStartDate < secondObjectStartDate) ? 1 : -1;
          }
          return -1;
        }
        if (subsection2.current) {
          if (subsection1.current) {
            return (firstObjectStartDate < secondObjectStartDate) ? 1 : -1;
          }
          return 1;
        }

        if ((subsection1.current || subsection2.current) && (firstObjectStartDate !== secondObjectStartDate)) {
          if (firstObjectStartDate < secondObjectStartDate) return 1;
          if (firstObjectStartDate > secondObjectStartDate) return -1;
          return 0;
        } if ((firstObjectEndDate === secondObjectEndDate) && (firstObjectStartDate !== secondObjectStartDate)) {
          if (firstObjectStartDate < secondObjectStartDate) return 1;
          if (firstObjectStartDate > secondObjectStartDate) return -1;
          return 0;
        } if (subsection1.editMode && ((secondObjectStartDate === 0 && secondObjectEndDate === 0)
        || ((firstObjectStartDate === secondObjectStartDate) && (firstObjectEndDate === secondObjectEndDate)))) {
          return -1;
        }
        if (firstObjectEndDate < secondObjectEndDate) return 1;
        if (firstObjectEndDate > secondObjectEndDate) return -1;
        return 0;
      },
    );
  }

  /**
   * Method to set focus and add the aria-label for the attribute.
   *
   * @param index of subsection for editing the experience.
   */
  private setFocusAndAttribute(index: number) {
    setTimeout(() => {
      $(`#bulbResumeExperienceJobTitle-${index}`).trigger('focus');

      // set the aria-label for the fast pass issue in primeng calendar
      this.renderer.setAttribute(
        document.querySelector('.p-checkbox-box'),
        'aria-label',
        this.checkboxLabel,
      );
    });
  }

  /**
   * Translate the keywords.
   */
  private translateKeywords() {
    this.endDatePlaceholder = this.$translate.translateInstant('resume_experience_end_date_placeholder');
    this.checkboxLabel = this.$translate.translateInstant('resume_experience_currently_working');
  }

  /**
   * Method to delete entry from subsections.
   * @param index - of the entry in the subsection.
   */
  public deleteEntry(event, index) {
    event.stopPropagation();
    this.bulbModalService.show({
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      titleCode: 'resume_delete_entry_confirmation_title',
      bodyCode: 'resume_delete_entry_confirmation_message',
      okButtonCode: 'button_confirm',
      cancelButtonCode: 'button_cancel',
      windowClass: 'updated-modal-design bulb-aero-modal bulb-resume-delete-entry-modal',
    }).then(() => {
      this.experienceSubSectionClone.splice(index, 1);
      this.experienceSection.subSections = this.experienceSubSectionClone;
      if (this.experienceSection.subSections.length === 0) {
        this.experienceSection.empty = true;
        this.isTabOpen = false;
        sessionStorage.experienceTabOpen = false;
        this.showAddEntryButton = false;
      } else {
        this.experienceSection.subSections.forEach((subsection: any) => {
          subsection.startDate = (subsection.startDate)
            ? moment(subsection.startDate).format('YYYY-MM-DD') : null;
          subsection.endDate = (subsection.endDate)
            ? moment(subsection.endDate).format('YYYY-MM-DD') : null;
        });
      }
      this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.experienceSubSectionClone);
      this.resumeComponent.save();
    });
  }

  /**
   * Callback method which is called when editor data is changed.
   * @param summaryData
   * @param subSectionIndex
   */
  updateSummaryData(summaryData, subSectionIndex) {
    this.experienceSubSectionClone[subSectionIndex].summary = summaryData;
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[subSectionIndex];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent, this.charLimitReached);
  }

  /**
   * Callback function when input model changes
   * @param $event
   * @param subSectionIndex
   */
  handleInputModelChanges($event, subSectionIndex) {
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[subSectionIndex];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent, this.charLimitReached);
  }

  /**
   * Callback function for character count.
   * @param charCount - current number of character in the editor.
   */
  handleLimitReached(charCount) {
    this.charLimitReached = (charCount > this.maxLength);
  }

  /**
   * Method to delete logo from subsection
   * @param $event
   * @param index
   */
  public deleteLogo(index: number) {
    this.experienceSubSectionClone[index].logoUrl = null;
    this.experienceSubSectionClone[index].logo = null;
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[index];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent);
  }

  /**
   *  Drag start function.
   * @param event event
   * @param section Dragged section
   */
  dragStart(event, section) {
    this.resumeComponent.dragStart(section);
  }

  /**
   * Drop function
   * @param event event
   * @param section  On dragged section
   */
  drop(event, section) {
    this.resumeComponent.drop(section);
  }

  /**
   * Add work highlights.
   * @param subSectionIndex index of array to add highlights
   */
  addWorkHighlight(subSectionIndex: number): void {
    this.experienceSubSectionClone[subSectionIndex].workHighlights.push('');
  }

  /**
   * Track dynamically created text area by index.
   *
   * @param index index of highlight
   * @param item highlight
   */
  trackByWorkHighlights(index: number, item: any) {
    return index;
  }
}
